import { useMemo, useState } from "react";
import { useLogoStore } from "../../state/logoStore";
import { suggestSlogans } from "../../engine/sloganSuggester";
import { ColorRow, Slider } from "./EditorControls";
import { PatternPicker } from "./Pickers";

// ============================================================
// Icon pattern panel — overlay stripes / dots / grid on the symbol
// (think AT&T globe, Pepsi swoosh, halftone treatments)
// ============================================================
export function IconPatternPanel() {
  const s = useLogoStore();
  return (
    <>
      <div className="section-label" style={{ marginTop: 16 }}>Symbol pattern</div>
      <div style={{ fontSize: 12, color: "#475569", marginBottom: 10, lineHeight: 1.5 }}>
        Overlay stripes, dots, grids, waves, or other textures on the symbol — like the AT&amp;T globe.
      </div>
      <PatternPicker
        selected={s.iconPattern}
        onPick={(id) => { s.pushHistory(); s.setStyle({ iconPattern: id }); }}
      />
      {s.iconPattern !== "none" && (
        <>
          <ColorRow
            label="Pattern color"
            color={s.iconPatternColor}
            onChange={(c) => { s.pushHistory(); s.setStyle({ iconPatternColor: c }); }}
          />
          <Slider
            label="Pattern density"
            min={0.5} max={2} step={0.1}
            value={s.iconPatternDensity}
            onChange={(v) => s.setStyle({ iconPatternDensity: v })}
            onCommit={() => s.pushHistory()}
          />
        </>
      )}
    </>
  );
}

// ============================================================
// History panel — undo/redo with a simple action list
// ============================================================
export function HistoryPanel() {
  const s = useLogoStore();
  const past = s.history.past.length;
  const future = s.history.future.length;
  return (
    <>
      <div className="section-label">History</div>
      <div className="history-controls">
        <button className="btn" onClick={() => s.undo()} disabled={!s.canUndo()}>↶ Undo ({past})</button>
        <button className="btn" onClick={() => s.redo()} disabled={!s.canRedo()}>Redo ({future}) ↷</button>
      </div>
      <div className="history-list">
        {past === 0 && future === 0 ? (
          <div className="history-empty">No history yet — make some changes and they'll show up here.</div>
        ) : (
          <>
            {past > 0 && (
              <div className="history-bucket">
                <div className="history-bucket-label">Past ({past})</div>
                {Array.from({ length: Math.min(past, 8) }).map((_, i) => (
                  <div key={`p-${i}`} className="history-row">
                    <span className="history-dot" />
                    <span>state {past - i}</span>
                  </div>
                ))}
              </div>
            )}
            {future > 0 && (
              <div className="history-bucket muted">
                <div className="history-bucket-label">Future ({future})</div>
                {Array.from({ length: Math.min(future, 4) }).map((_, i) => (
                  <div key={`f-${i}`} className="history-row">
                    <span className="history-dot ghost" />
                    <span>state +{i + 1}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12, lineHeight: 1.5 }}>
        Tip: <kbd>⌘Z</kbd> undo · <kbd>⌘⇧Z</kbd> redo. History keeps the last 50 changes.
      </div>
    </>
  );
}

// ============================================================
// Inline slogan suggestions (shown inside the Slogan panel)
// ============================================================
export function SloganSuggestionsInline() {
  const { onboarding, title, setSlogan, pushHistory } = useLogoStore();
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState(0);
  const ideas = useMemo(
    () => suggestSlogans(onboarding.industry, title, 6),
    [onboarding.industry, title, seed]
  );

  if (!open) {
    return (
      <button className="btn" style={{ width: "100%", marginTop: 8 }} onClick={() => setOpen(true)}>
        ✨ Suggest slogans
      </button>
    );
  }

  return (
    <div className="slogan-suggestions" style={{ marginTop: 8 }}>
      <div className="slogan-suggestions-head">
        <span>{onboarding.industry ? `Tailored to ${onboarding.industry}` : "Tap any to use"}</span>
        <span style={{ display: "flex", gap: 6 }}>
          <button className="link-btn" onClick={() => setSeed((v) => v + 1)}>↻ More</button>
          <button className="link-btn" onClick={() => setOpen(false)}>Close</button>
        </span>
      </div>
      <div className="slogan-list">
        {ideas.map((idea, i) => (
          <button
            key={i}
            className="slogan-pill"
            onClick={() => { pushHistory(); setSlogan(idea); setOpen(false); }}
          >{idea}</button>
        ))}
      </div>
    </div>
  );
}
